import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'
import { Card, CardBody, CardHeader } from './ui/Card'
import { DownloadCsvButton } from './ui/DownloadCsvButton'
import { toCsv, type CsvColumn } from '../lib/csv'
import { downloadCsv } from '../lib/download'
import type { KgContradiction } from '../api/types'

/**
 * Where the run disagreed with the knowledge graph, and could not fix it.
 *
 * The standing case: the graph asserts `hasKnownLigand=false` for peptide
 * deformylase 1B while actinonin inhibition is published and experimental
 * complexes are in the PDB. A run that noticed had nowhere to put the
 * observation, so it either scored against a value it knew to be false or argued
 * with it in prose nothing downstream could read.
 *
 * Two things this panel must not imply, both stated in the copy rather than left
 * to the reader:
 *
 *  - **The graph was not fixed.** `crop-protection-kg` is built and owned by
 *    another repository and nothing here writes to Neptune. These are candidate
 *    corrections for a curator.
 *  - **The disputed value is what the run scored on.** Filing changes no rank, so
 *    a row here never explains a target's position on the board above.
 *
 * Collapsed by default, like the dead ends: it is the run's disagreement with its
 * inputs, not its result. The count stays visible while collapsed, because a reader
 * has to be able to see that the run disputed *something* without opening it.
 *
 * `loaded === false` is not `total === 0`, exactly as in `DeadEndTable`: "this run
 * disputed nothing" is a result, and "could not read the ledger" is a fact about this
 * page's request. The header's own line keeps them apart too, not only the body's.
 */
export function KgContradictionTable({
  contradictions,
  total,
  loaded,
}: {
  contradictions: KgContradiction[]
  total: number
  /** False while the fetch is outstanding, or if it failed. See the empty state. */
  loaded: boolean
}) {
  const [open, setOpen] = useState(false)

  const download = () => {
    downloadCsv(
      `kg-contradictions-${contradictions[0]?.run_id ?? 'export'}.csv`,
      toCsv(csvRows(contradictions), CSV_COLUMNS),
    )
  }

  return (
    <Card>
      <CardHeader
        eyebrow="Disputed graph values"
        title={
          <span className="flex items-baseline gap-2">
            Where this run contradicted the graph
            {loaded && (
              <span className="font-mono text-2xs tabular-nums text-slate-500">
                {total}
              </span>
            )}
          </span>
        }
        description={
          loaded
            ? total === 0
              ? 'This run found no graph field it could contradict with a named external source.'
              : `${total} field${total === 1 ? '' : 's'} contradicted by external evidence. Candidate corrections for a curator — nothing in this system writes to the graph, and the disputed value is still what this run scored on.`
            : // Not "0" and not "disputed nothing". The fetch has not answered.
              'Waiting on the contradiction ledger for this run.'
        }
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {open ? 'Hide' : 'Show'}
          </Button>
        }
      />

      {open && (
        <CardBody>
          {/* Three empty states, deliberately, exactly as for the dead ends.
              "Disputed nothing" is a result about the run; "could not read" is a
              fact about this request; and a run predating the ledger has no rows
              and never will. Showing any of them as another misrepresents the
              state of the data. */}
          {!loaded ? (
            <p className="text-sm text-slate-500">
              Could not read this run&apos;s contradiction ledger. That is not the
              same as the graph having gone unchallenged.
            </p>
          ) : total === 0 ? (
            <p className="text-sm text-slate-400">
              This run found no knowledge-graph field it could contradict with a
              named external source — the normal outcome. Runs recorded before
              this was tracked show the same thing and mean something else.
            </p>
          ) : (
            <>
              <p className="mb-3 text-xs text-slate-500">
                Candidate corrections for the ontology, not applied ones:{' '}
                <span className="text-slate-400">
                  nothing in this system writes to the graph
                </span>
                . The disputed value is still what this run scored on, so a row
                here does not explain a target&apos;s rank. Each row carries at
                least one named external source — a filing without one is refused.
              </p>
              <div className="mb-2 flex items-center justify-end">
                <DownloadCsvButton
                  selectedCount={0}
                  rowCount={contradictions.length}
                  noun="disputed values"
                  onDownload={download}
                />
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-800">
                      {[
                        'Protein',
                        'Field',
                        'Graph says',
                        'Evidence says',
                        'Sources',
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {contradictions.map((c) => (
                      <tr
                        key={`${c.accession}:${c.kg_field}`}
                        className="border-b border-slate-800/70 align-top last:border-b-0"
                      >
                        <td className="px-3 py-2">
                          <div className="text-slate-200">
                            {c.protein ?? c.accession}
                          </div>
                          <div className="font-mono text-2xs text-slate-500">
                            {c.accession}
                            {c.hypothesis_id && ` · ${c.hypothesis_id}`}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-mono text-xs text-slate-300">
                            {c.kg_field}
                          </div>
                          {c.confidence && (
                            /* Confidence that the GRAPH is wrong — not that the
                               target is good. The title says so, because the two
                               judgements read identically in a badge. */
                            <div
                              className="mt-1"
                              title="Confidence that the graph is wrong, not that the target is good"
                            >
                              <Badge
                                className="px-1.5 py-0 text-2xs"
                                variant={
                                  c.confidence === 'high' ? 'yellow' : 'default'
                                }
                              >
                                {c.confidence}
                              </Badge>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-400">
                          {c.kg_value ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-300">
                          {c.contradicting_value ?? '—'}
                          {c.note && (
                            <div className="mt-1 text-xs text-slate-500">
                              {c.note}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <ul className="space-y-1">
                            {(c.evidence ?? []).map((e, i) => (
                              <li key={`e${i}`} className="text-xs">
                                {e.url ? (
                                  <a
                                    href={e.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-mono text-brand-300 hover:text-brand-200 hover:underline"
                                  >
                                    {e.id}
                                  </a>
                                ) : (
                                  <span className="font-mono text-slate-300">
                                    {e.id}
                                  </span>
                                )}
                                <span className="ml-1 text-slate-500">
                                  {e.type}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* No silent caps: a page that did not cover the run says so. */}
              {contradictions.length < total && (
                <p className="mt-2 text-xs text-slate-500">
                  Showing {contradictions.length} of {total}.
                </p>
              )}
            </>
          )}
        </CardBody>
      )}
    </Card>
  )
}

/**
 * One CSV record: a disputed field paired with one of the sources cited against
 * it.
 *
 * One row per source, for `DeadEndTable`'s reason: a filing may cite a paper *and*
 * a PDB entry, the two are what makes the contradiction credible, and joining them
 * into one cell leaves a curator unable to filter by source type — which is the
 * first thing a curator does. Every row always has at least one source (the filing
 * tool refuses a row without), so unlike the dead ends there is no empty-source
 * case to preserve.
 */
interface CsvRow {
  c: KgContradiction
  e: KgContradiction['evidence'][number]
}

function csvRows(contradictions: readonly KgContradiction[]): CsvRow[] {
  return contradictions.flatMap((c) => (c.evidence ?? []).map((e) => ({ c, e })))
}

/**
 * The caveat, in a column on every row rather than as a comment line above the
 * header.
 *
 * A leading comment would break any parser that reads line 1 as the header — and
 * the point is precisely that this file gets read by tooling, not just opened. A
 * column instead follows the rule the harness already applies to
 * `runs_identified`: the caveat ships with **every** read rather than being stated
 * once where it can be scrolled past, cropped out of a screenshot, or lost when a
 * curator filters the sheet down to the rows they intend to act on. That last case
 * is the one that matters here: the file is a work list for someone with write
 * access to the graph this system deliberately cannot touch.
 */
const CAVEAT =
  'Candidate correction, NOT applied. Nothing in the discovery system writes to ' +
  'the knowledge graph. The disputed value is what the run scored on, so this row ' +
  'does not explain any rank.'

const CSV_COLUMNS: readonly CsvColumn<CsvRow>[] = [
  { header: 'run_id', value: ({ c }) => c.run_id },
  { header: 'accession', value: ({ c }) => c.accession },
  { header: 'protein', value: ({ c }) => c.protein },
  { header: 'kg_field', value: ({ c }) => c.kg_field },
  { header: 'kg_value', value: ({ c }) => c.kg_value },
  { header: 'contradicting_value', value: ({ c }) => c.contradicting_value },
  // Confidence that the GRAPH is wrong, not that the target is good. The header
  // says which, because the two read identically in a spreadsheet cell and only
  // one of them is a statement about a target.
  { header: 'confidence_graph_is_wrong', value: ({ c }) => c.confidence },
  { header: 'note', value: ({ c }) => c.note },
  { header: 'source_type', value: ({ e }) => e.type },
  { header: 'source_id', value: ({ e }) => e.id },
  { header: 'source_url', value: ({ e }) => e.url },
  { header: 'source_note', value: ({ e }) => e.note },
  { header: 'hypothesis_id', value: ({ c }) => c.hypothesis_id },
  { header: 'created_at', value: ({ c }) => c.created_at },
  { header: 'caveat', value: () => CAVEAT },
]
